import {
  Box,
  SimpleGrid,
  Text,
  Badge,
  Icon,
  useColorModeValue,
} from '@chakra-ui/react';
import { FaFileAlt, FaBriefcase, FaFileSignature } from 'react-icons/fa';
import type { IconType } from 'react-icons';
import type { MVRRequestTransactionType } from 'api';

interface MvrTypeOption {
  value: MVRRequestTransactionType;
  title: string;
  description: string;
  tag: string;
  icon: IconType;
}

const MVR_TYPE_OPTIONS: MvrTypeOption[] = [
  {
    value: 'PLPC',
    title: 'Personal Lines',
    description: 'Policy Change',
    tag: 'Personal',
    icon: FaFileAlt,
  },
  {
    value: 'CLNB',
    title: 'Commercial Lines',
    description: 'New Business',
    tag: 'Commercial',
    icon: FaBriefcase,
  },
  {
    value: 'CLPC',
    title: 'Commercial Lines',
    description: 'Policy Change',
    tag: 'Commercial',
    icon: FaFileSignature,
  },
];

interface MvrTypeSelectorProps {
  selectedType: MVRRequestTransactionType | null;
  onSelect: (type: MVRRequestTransactionType) => void;
}

const MvrTypeSelector = ({ selectedType, onSelect }: MvrTypeSelectorProps) => {
  const textColor = useColorModeValue('gray.700', 'white');
  const borderColor = useColorModeValue('gray.200', 'whiteAlpha.200');
  const hoverBorderColor = useColorModeValue('gray.300', 'whiteAlpha.300');
  const selectedBorderColor = useColorModeValue('brand.500', 'brand.400');
  const selectedBg = useColorModeValue('brand.50', 'whiteAlpha.100');

  return (
    <Box role="radiogroup" aria-label="MVR request transaction type">
      <SimpleGrid columns={{ base: 1, md: 3 }} spacing={4}>
        {MVR_TYPE_OPTIONS.map((option) => {
          const isSelected = selectedType === option.value;
          return (
            <Box
              key={option.value}
              as="button"
              type="button"
              role="radio"
              aria-checked={isSelected}
              onClick={() => onSelect(option.value)}
              textAlign="left"
              p={5}
              borderWidth="2px"
              borderRadius="12px"
              borderColor={isSelected ? selectedBorderColor : borderColor}
              bg={isSelected ? selectedBg : 'transparent'}
              _hover={{
                borderColor: isSelected
                  ? selectedBorderColor
                  : hoverBorderColor,
              }}
              _focus={{ boxShadow: 'outline', outline: 'none' }}
              transition="all 0.15s"
            >
              <Icon
                as={option.icon}
                boxSize={6}
                color={isSelected ? selectedBorderColor : 'gray.400'}
                mb={3}
              />
              <Text fontWeight="700" color={textColor}>
                {option.title}
              </Text>
              <Text fontSize="sm" color="gray.500" mb={3}>
                {option.description}
              </Text>
              <Badge colorScheme={isSelected ? 'brand' : 'gray'}>
                {option.tag}
              </Badge>
            </Box>
          );
        })}
      </SimpleGrid>
    </Box>
  );
};

export default MvrTypeSelector;
